import { useEffect, useMemo, useState } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import gridColumns from "helpers/gridColumns";
import { useTheme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Pagination from '@mui/material/Pagination';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import TableViewIcon from '@mui/icons-material/TableView';
import Button from '@mui/material/Button';
import { useExport } from 'context/ExportContext';

const CALF_GREEN       = '#8ebf63';
const CALF_GREEN_DARK  = '#39a82d';
const CALF_GREEN_LIGHT = '#f0f7e8';
const ROW_ALT          = '#f7faf3';
const ROW_HOVER        = '#e8f4d9';
const EXPORT_ROW_LIMIT = 10000;

const GrillaEffect = ({
  data,
  updateData,
  filters,
  updateFilters,
  hiddenColumns = null,
  customTopToolbar = null,
  idRemoto = null,
  baseURI = null,
  method = 'POST',
  colsHeaders = [],
  useCustomFilter = false,
  displayColumnDefOptions,
  handleRevertir,
  handleConciliar,
  onRowSelectionChange,
  isLoading: isLoadingProp,
  additionalParams = {},
  rowSelectionEnabled = false,
  defaultSorting = [],
  renderDetailPanel = null,
}) => {
  //data and fetching state
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  //table state
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState(defaultSorting ?? []);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [columnOrder, setColumnOrder] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  // Mobile view state
  const storedMode = localStorage.getItem('grillaViewMode') || 'cards';
  const [viewMode, setViewMode] = useState(storedMode);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { startExport } = useExport();

  const handleExport = (format) => {
    startExport({
      idRemoto,
      format,
      filters,
      sorting,
      globalFilter,
      colsHeaders,
      label: `Query ${idRemoto}`,
    });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleViewModeChange = (_, newMode) => {
    if (!newMode) return;
    setViewMode(newMode);
    localStorage.setItem('grillaViewMode', newMode);
  };

  const formatCellValue = (columnId, value) => {
    if (value === null || value === undefined) return '—';
    if (
      columnId.toLowerCase().includes('importe') ||
      columnId === 'debito' ||
      columnId === 'credito'
    ) {
      const num = Number(String(value).replace(/[^0-9\-,.]/g, '').replace(',', '.'));
      if (isNaN(num)) return value;
      return '$ ' + num.toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }
    if (columnId.toLowerCase().includes('fecha')) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        });
      }
    }
    return String(value);
  };

  const fetchData = async () => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }
    let currentFilters = [...filters];
    currentFilters = currentFilters.filter(filter => {
      return Object.keys(colsHeaders).some(columnKey => columnKey === filter.id)
    });
    const baseURL = baseURI ? baseURI : `${process.env.REACT_APP_API_URL}/querysextgrilla/`;
    console.log("BaseURL en GrillaEffect: ", baseURL);
    const queryParams = {
      id: idRemoto ? idRemoto : 21,
      page: pagination.pageIndex,
      size: pagination.pageSize,
      filters: JSON.stringify(currentFilters ?? []),
      globalFilter: globalFilter ?? '',
      sorting: JSON.stringify(sorting ?? []),
      custom_filter: useCustomFilter,
      ...additionalParams
    };
    const urlWithParams = new URL(baseURL);
    Object.keys(queryParams).forEach(key => urlWithParams.searchParams.append(key, queryParams[key]));

      try {
        const response = await fetch(urlWithParams, {
          method: method,
          headers: {
            "Content-Type": "application/json",
            "Authorization": localStorage.getItem('token'),
          }
        });
        const json = await response.json();
        updateData(json.data);
        if(json.meta.totalRowCount !== undefined){
          setRowCount(json.meta.totalRowCount);
        }
      } catch (error) {
        setIsError(true);
        console.error(error);
        return;
      }
      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };
  useEffect(() => {
    fetchData();
  }, [
    filters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    JSON.stringify(additionalParams),
  ]);
  const refreshIconComponent = function () {
    const overLimit = rowCount > EXPORT_ROW_LIMIT;
    const limitMsg  = `No disponible: ${rowCount.toLocaleString('es-AR')} filas superan el límite de exportación (${EXPORT_ROW_LIMIT.toLocaleString('es-AR')})`;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip arrow title="Actualizar">
          <IconButton onClick={() => fetchData()} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Tooltip arrow title={overLimit ? limitMsg : 'Exportar CSV'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
              disabled={overLimit}
              sx={{ fontSize: '0.72rem', py: 0.5 }}
            >
              CSV
            </Button>
          </span>
        </Tooltip>

        <Tooltip arrow title={
          overLimit
            ? limitMsg
            : rowCount > 1048576
              ? `No disponible: supera el límite de Excel (1.048.576 filas)`
              : 'Exportar Excel'
        }>
          <span>
            <Button
              size="small"
              variant="outlined"
              color="success"
              startIcon={<TableViewIcon />}
              onClick={() => handleExport('xlsx')}
              disabled={overLimit || rowCount > 1048576}
              sx={{ fontSize: '0.72rem', py: 0.5 }}
            >
              Excel
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };
  const columns = gridColumns(data, colsHeaders, filters, handleRevertir, handleConciliar);
  const table = useMaterialReactTable({
    columns,
    data,
    enableFilters: true,
    enableColumnFilters: true,
    enableRowSelection: rowSelectionEnabled,
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);
      if (onRowSelectionChange) {
        onRowSelectionChange(
          typeof updater === 'function' ? updater(rowSelection) : updater,
          table
        );
      }
    },
    state: {
      filters,
      globalFilter,
      isLoading: isLoadingProp ?? isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
      rowSelection,
      ...(typeof colsHeaders === 'object' && !Array.isArray(colsHeaders) && {
        columnOrder: [...(renderDetailPanel ? ['mrt-row-expand'] : []), 'mrt-row-select', ...Object.keys(colsHeaders)]
      })
    },
    initialState: {
      density: 'compact',
      // MRT usa Object.values(columnVisibility) en el menú de acciones de columna (⋮);
      // si llega null (grillas que no pasan hiddenColumns) rompe con "Cannot convert
      // undefined or null to object" y tumba la vista (Mantis #253).
      columnVisibility: hiddenColumns ?? {},
      showColumnFilters: true,
      columnFilters: filters,
      ...(typeof colsHeaders === 'object' && !Array.isArray(colsHeaders) && {
        columnOrder: [...(renderDetailPanel ? ['mrt-row-expand'] : []), 'mrt-row-select', ...Object.keys(colsHeaders)]
      })
    },
    ...(renderDetailPanel && { renderDetailPanel }),
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    enableMultiSort: true,
    density: 'compact',
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#f5f5f5',
        color: '#161616',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        borderBottom: `3px solid ${CALF_GREEN}`,
        '& .MuiButtonBase-root': { color: '#161616' },
        '& .MuiTableSortLabel-icon': { color: '#161616 !important' },
        '& .MuiIconButton-root': { color: '#161616' },
        '& input': { fontSize: '0.8rem' },
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor:
          row.index % 2 === 0 ? '#ffffff' : ROW_ALT,
        '&:hover td': {
          backgroundColor: ROW_HOVER,
        },
        transition: 'background-color 0.15s ease',
      },
    }),
    muiTableBodyCellProps: {
      sx: {
        fontSize: '0.85rem',
        borderBottom: '1px solid #eee',
      },
    },
    muiTableBodyProps: {
      children: isLoading ? (
        <tr style={{ height: '200px' }}>
          <td />
        </tr>
      ) : undefined,
    },
    muiTableContainerProps: {
      sx: { overflowX: 'auto', maxWidth: '100%' },
    },
    muiToolbarAlertBannerProps: isError
      ? {
        color: 'error',
        children: 'Error loading data',
      }
      : undefined,
    onColumnFiltersChange: updateFilters,
    onColumnOrderChange: setColumnOrder,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: typeof customTopToolbar === 'function'
      ? customTopToolbar
      : () => (customTopToolbar ?? refreshIconComponent()),
    muiTopToolbarProps: {
      sx: {
        backgroundColor: '#fafafa',
        borderBottom: `2px solid ${CALF_GREEN}`,
        flexWrap: 'wrap',
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: '#fafafa',
        flexWrap: 'wrap',
      },
    },
    displayColumnDefOptions,
    rowCount,
  });

  const renderMobileCards = () => {
    const columnKeys = typeof colsHeaders === 'object' && !Array.isArray(colsHeaders)
      ? Object.keys(colsHeaders)
      : [];

    return (
      <Box>
        {/* Toolbar mobile */}
        <Box sx={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', mb: 1, gap: 1
        }}>
          <Tooltip title="Filtros">
            <IconButton
              onClick={() => setFiltersOpen(!filtersOpen)}
              color={filtersOpen ? 'primary' : 'default'}
              size="small"
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="cards">
              <Tooltip title="Vista cards">
                <ViewAgendaIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Vista tabla">
                <TableRowsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Actualizar">
            <IconButton onClick={() => fetchData()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Panel de filtros colapsable */}
        <Collapse in={filtersOpen}>
          <Box sx={{
            p: 2, mb: 2, borderRadius: 2,
            bgcolor: 'grey.50', border: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="caption" color="text.secondary"
              fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              FILTROS
            </Typography>
            {columnKeys
              .filter(key => colsHeaders[key]?.enableColumnFilter !== false)
              .map(key => {
                const currentFilter = filters.find(f => f.id === key);
                return (
                  <Box key={key} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {colsHeaders[key]?.caption || key}
                    </Typography>
                    <input
                      style={{
                        width: '100%', padding: '6px 10px',
                        borderRadius: 6, border: '1px solid #ddd',
                        fontSize: 13, marginTop: 2, boxSizing: 'border-box'
                      }}
                      value={currentFilter?.value || ''}
                      onChange={(e) => {
                        const newFilters = filters.filter(f => f.id !== key);
                        if (e.target.value) {
                          newFilters.push({ id: key, value: e.target.value });
                        }
                        updateFilters(newFilters);
                      }}
                      placeholder={`Filtrar ${colsHeaders[key]?.caption || key}...`}
                    />
                  </Box>
                );
              })}
          </Box>
        </Collapse>

        {/* Cards */}
        {isLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Cargando...</Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">Sin datos</Typography>
          </Box>
        ) : (
          data.map((row, idx) => (
            <Card
              key={idx}
              variant="outlined"
              sx={{
                mb: 1.5,
                borderRadius: 2,
                borderLeft: `4px solid ${CALF_GREEN}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {columnKeys.map(key => (
                  <Box key={key} sx={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'baseline', py: 0.3,
                    borderBottom: '1px solid', borderColor: 'grey.100',
                    '&:last-child': { borderBottom: 'none' }
                  }}>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontWeight: 600, textTransform: 'uppercase',
                            fontSize: '0.65rem', mr: 1 }}>
                      {colsHeaders[key]?.caption || key}
                    </Typography>
                    <Typography variant="body2"
                      sx={{ fontWeight:
                        key.toLowerCase().includes('importe') ? 600 : 400,
                        textAlign: 'right'
                      }}>
                      {formatCellValue(key, row[key])}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))
        )}

        {/* Paginación */}
        <Box sx={{ display: 'flex', justifyContent: 'center',
                   alignItems: 'center', mt: 2, gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {rowCount} registros
          </Typography>
          <Pagination
            count={Math.ceil(rowCount / pagination.pageSize)}
            page={pagination.pageIndex + 1}
            onChange={(_, page) =>
              setPagination(prev => ({ ...prev, pageIndex: page - 1 }))
            }
            size="small"
            color="primary"
          />
        </Box>
      </Box>
    );
  };

  if (isMobile && viewMode === 'cards') {
    return renderMobileCards();
  }

  return (
    <Box>
      {isMobile && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="cards">
              <Tooltip title="Vista cards">
                <ViewAgendaIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="table">
              <Tooltip title="Vista tabla">
                <TableRowsIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <MaterialReactTable table={table} />
    </Box>
  );
}

export default GrillaEffect;
